<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SupplierDealReportHeader from './SupplierDealReportHeader.vue';
import SupplierDealReportHero from './SupplierDealReportHero.vue';
import SupplierDealReportLeaderboard from './SupplierDealReportLeaderboard.vue';
import SupplierDealReportMoney from './SupplierDealReportMoney.vue';
import SupplierDealReportOfferCard from './SupplierDealReportOfferCard.vue';
import SupplierDealReportReach from './SupplierDealReportReach.vue';
import { useSupplierDealReport } from '@/composables/useSupplierDealReport.js';

const { t } = useI18n({ useScope: 'global' });

// Tests stub the module (vi.mock('@/composables/useSupplierDealReport.js', ...))
// rather than injecting a prop — same pattern as PoolaPage.host.test.js does for
// usePoolaDeal.
const { dealTitle, dateRange, viewModel, loading, forbidden, notAvailable, hasNoOffers } =
  useSupplierDealReport();

const showEmptyNotAvailable = computed(
  () => !loading.value && !forbidden.value && notAvailable.value,
);
const showEmptyNoOffers = computed(
  () => !loading.value && !forbidden.value && !notAvailable.value && hasNoOffers.value,
);
const showReport = computed(
  () =>
    !loading.value &&
    !forbidden.value &&
    !notAvailable.value &&
    !hasNoOffers.value &&
    viewModel.value != null,
);
</script>

<template>
  <div class="supplier-deal-report-panel">
    <div v-if="loading" class="report-skeleton" data-report-state="loading" aria-hidden="true">
      <f7-skeleton-block
        tag="div"
        width="70%"
        height="22px"
        border-radius="6px"
        effect="wave"
      ></f7-skeleton-block>
      <f7-skeleton-block
        tag="div"
        width="100%"
        height="130px"
        border-radius="16px"
        effect="wave"
      ></f7-skeleton-block>
      <f7-skeleton-block
        tag="div"
        width="100%"
        height="90px"
        border-radius="16px"
        effect="wave"
      ></f7-skeleton-block>
      <f7-skeleton-block
        tag="div"
        width="100%"
        height="90px"
        border-radius="16px"
        effect="wave"
      ></f7-skeleton-block>
    </div>

    <!-- Not a supplier — the entry point already gates this; render nothing. -->
    <template v-else-if="forbidden" />

    <div v-else-if="showEmptyNotAvailable" class="report-empty" data-report-state="not-available">
      <p class="report-empty__text">{{ t('poola.report.empty.noClosedDeals') }}</p>
    </div>

    <div v-else-if="showEmptyNoOffers" class="report-empty" data-report-state="no-offers">
      <p class="report-empty__text">
        {{ t('poola.report.empty.noOffersInDeal', { deal: dealTitle }) }}
      </p>
    </div>

    <template v-else-if="showReport">
      <supplier-deal-report-header
        :deal-title="dealTitle"
        :date-range="dateRange"
      ></supplier-deal-report-header>
      <supplier-deal-report-hero :hero="viewModel.hero"></supplier-deal-report-hero>
      <supplier-deal-report-reach :reach="viewModel.reach"></supplier-deal-report-reach>
      <supplier-deal-report-money :money="viewModel.money"></supplier-deal-report-money>
      <supplier-deal-report-leaderboard
        :rows="viewModel.leaderboard"
        :my-rank="viewModel.myRank"
        :total-suppliers="viewModel.totalSuppliers"
        :deal-title="dealTitle"
      ></supplier-deal-report-leaderboard>
      <div class="report-offers" data-report-section="offers">
        <h2 class="report-section-title">{{ t('poola.report.table.title') }}</h2>
        <supplier-deal-report-offer-card
          v-for="offer in viewModel.perOffer"
          :key="offer.id"
          :offer="offer"
        ></supplier-deal-report-offer-card>
      </div>
    </template>
  </div>
</template>

<style scoped>
.supplier-deal-report-panel {
  /* ── Local report palette, mapped onto the app's real design tokens ──────
     teal = the بولكريديت/success story, violet = brand/members, gold = the
     completed-offers tile. Overridden below for `.dark` (F7 darkMode:'auto'
     adds .dark to <html>; the base --poolit-* tokens do NOT auto-swap, see
     CLAUDE.md's CSS token note — only a handful of *-dark suffixed tokens and
     F7's own --f7-* vars do). */
  --report-teal: var(--poolit-success-600, #059669);
  --report-teal-deep: var(--poolit-success-700, #047857);
  --report-teal-bg: color-mix(
    in srgb,
    var(--poolit-success-500, #10b981) 12%,
    var(--poolit-bg-primary, #fff)
  );
  --report-teal-line: color-mix(in srgb, var(--poolit-success-500, #10b981) 30%, transparent);

  --report-violet: var(--poolit-primary-600, #7a3ef0);
  --report-violet-deep: var(--poolit-primary-700, #6621e2);
  --report-violet-bg: color-mix(
    in srgb,
    var(--poolit-primary-500, #8e5bfe) 12%,
    var(--poolit-bg-primary, #fff)
  );
  --report-violet-line: color-mix(in srgb, var(--poolit-primary-500, #8e5bfe) 30%, transparent);

  --report-gold-deep: #92700c;
  --report-gold-bg: linear-gradient(135deg, #fef3c7, #fde68a);
  --report-gold-line: rgba(212, 175, 55, 0.35);

  --report-surface: var(--f7-card-bg-color, var(--poolit-bg-primary, #fff));
  --report-surface-2: var(--poolit-bg-secondary, #f5f7fa);
  --report-hairline: var(--poolit-border-color, #e2e8f0);
  --report-text: var(--f7-text-color, #1f2937);
  --report-text-soft: var(--f7-text-color-secondary, #6b7280);
  --report-text-mute: var(--poolit-text-placeholder, #9ca3af);

  display: flex;
  flex-direction: column;
  gap: var(--poolit-spacing-lg, 24px);
  padding-block: var(--poolit-spacing-md, 16px);
}

.dark .supplier-deal-report-panel {
  --report-teal-bg: color-mix(in srgb, var(--poolit-success-500, #10b981) 22%, transparent);
  --report-teal-line: color-mix(in srgb, var(--poolit-success-500, #10b981) 40%, transparent);
  --report-violet-bg: color-mix(in srgb, var(--poolit-primary-500, #8e5bfe) 22%, transparent);
  --report-violet-line: color-mix(in srgb, var(--poolit-primary-500, #8e5bfe) 40%, transparent);
  --report-gold-deep: #f3d28c;
  --report-gold-bg: rgba(228, 172, 56, 0.16);
  --report-gold-line: rgba(228, 172, 56, 0.4);
  --report-hairline: var(--poolit-border-color-on-dark, #2c2c2c);
  --report-surface-2: var(--poolit-bg-tertiary-dark, #2c2c2c);
}

.report-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-inline: var(--poolit-spacing-md, 16px);
}

.report-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 28px;
}

.report-empty__text {
  margin: 0;
  max-width: 320px;
  color: var(--report-text-soft);
  font-size: var(--poolit-font-size-body, 1rem);
  line-height: 1.6;
}

.report-offers {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding-inline-start: var(--poolit-spacing-md, 16px);
  padding-inline-end: 0;
}

.report-offers .report-section-title {
  margin: 2px 0 0;
  padding-inline-end: var(--poolit-spacing-md, 16px);
  font-size: 15px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
}

.report-offers :deep(.report-offer-card) {
  margin-inline-start: 0;
}
</style>
